import { getHubSpotSessionToken } from '@/lib/hubspotSession';

export interface HubSpotContext {
  portalId?: string;
  dealId?: string;
}

/** Link naar een record in HubSpot; 0-1 = contact, 0-2 = bedrijf, 0-3 = deal. */
export function hubspotRecordUrl(portalId: string, type: '0-1' | '0-2' | '0-3', id: string) {
  return `https://app.hubspot.com/contacts/${encodeURIComponent(portalId)}/record/${type}/${encodeURIComponent(id)}`;
}

/**
 * Sluit de iframe-modal waarin de HubSpot-kaart de app opende. De kaart laadt daarna de
 * offertes opnieuw en toont bij een net gepubliceerde offerte de knop om hem te mailen.
 */
export function closeHubSpotModal(): void {
  window.parent.postMessage(JSON.stringify({ action: 'DONE' }), '*');
}

const PORTAL_STORAGE_KEY = 'hubspot_portal_id';
const DEAL_STORAGE_KEY = 'hubspot_deal_id';
const TOKEN_STORAGE_KEY = 'hubspot_token';

/**
 * Parse portalId en recordId (dealId bij objectType 0-3) uit HubSpot record URL.
 * Voorbeeld: https://app-eu1.hubspot.com/contacts/25411680/record/0-3/478738797797/
 */
function parseFromHubSpotReferrer(referrer: string): { portalId?: string; dealId?: string } {
  try {
    const match = referrer.match(/hubspot\.com\/contacts\/(\d+)\/record\/(\d+-\d+)\/(\d+)/);
    if (!match) return {};
    const [, portalId, objectTypeId, recordId] = match;
    if (!portalId || !recordId) return {};
    // 0-3 = deals, 0-1 = contacts, 0-2 = companies. Bij deal is recordId de dealId.
    const dealId = objectTypeId === '0-3' ? recordId : undefined;
    return { portalId, dealId: dealId || recordId };
  } catch {
    return {};
  }
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const normalized = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = normalized.length % 4;
    const padded = pad ? normalized + '='.repeat(4 - pad) : normalized;
    const json = atob(padded);
    const parsed = JSON.parse(json);
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

/** Haal hubspotPortalId uit de frontend JWT (zonder signature-verify). */
export function getPortalIdFromAuthToken(token?: string | null): string | undefined {
  const raw = token ?? getHubSpotSessionToken();
  if (!raw) return undefined;
  const payload = decodeJwtPayload(raw);
  const portalId = payload?.hubspotPortalId;
  return typeof portalId === 'string' && portalId.trim() ? portalId.trim() : undefined;
}

export function hasHubSpotAuthToken(): boolean {
  if (typeof localStorage === 'undefined') return false;
  return Boolean(getHubSpotSessionToken());
}

export function storeHubSpotAuth(token: string, portalId?: string): void {
  localStorage.setItem(TOKEN_STORAGE_KEY, token);
  if (portalId) {
    localStorage.setItem(PORTAL_STORAGE_KEY, portalId);
  }
}

export const HUBSPOT_AUTH_MESSAGE = 'hubspot-auth';

export interface HubSpotAuthMessage {
  type: typeof HUBSPOT_AUTH_MESSAGE;
  token?: string;
  portalId?: string;
  error?: string;
}

export type HubSpotAuthResult =
  | { status: 'connected' }
  | { status: 'cancelled' }
  | { status: 'popup_blocked' }
  | { status: 'error'; message?: string };

interface ReconnectUrlOptions {
  /** Laat de callback zichzelf sluiten en de tokens terugsturen naar het openende venster. */
  popup?: boolean;
}

export function buildHubSpotReconnectUrl(
  nextPath = '/quotes',
  options: ReconnectUrlOptions = {}
): string {
  const apiBase = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '');
  const authBase = apiBase.endsWith('/api') ? apiBase : `${apiBase}/api`;
  const statePayload = options.popup
    ? { source: 'hubspot_card', close: true }
    : { source: 'hubspot_card', next: nextPath };
  const state = btoa(JSON.stringify(statePayload))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');
  return `${authBase}/hubspot/auth/url?redirect=1&state=${encodeURIComponent(state)}`;
}

/**
 * HubSpot weigert zijn inlogpagina in een iframe te tonen, dus binnen de HubSpot-card
 * levert een gewone redirect een leeg scherm op. Een popup is een top-level venster en
 * stuurt de tokens via postMessage terug, zodat ze in de storage van dit venster landen.
 */
export function connectHubSpot(): Promise<HubSpotAuthResult> {
  const popup = window.open(
    buildHubSpotReconnectUrl('/quotes', { popup: true }),
    'hubspot-auth',
    'width=620,height=780'
  );
  if (!popup) return Promise.resolve({ status: 'popup_blocked' });

  return new Promise((resolve) => {
    let settled = false;

    const cleanup = () => {
      window.removeEventListener('message', handleMessage);
      window.clearInterval(closedTimer);
    };

    const settle = (result: HubSpotAuthResult) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(result);
    };

    function handleMessage(event: MessageEvent) {
      if (event.origin !== window.location.origin) return;
      const data = event.data as HubSpotAuthMessage | undefined;
      if (!data || data.type !== HUBSPOT_AUTH_MESSAGE) return;

      if (data.token) {
        storeHubSpotAuth(data.token, data.portalId);
        settle({ status: 'connected' });
        return;
      }
      settle({ status: 'error', message: data.error });
    }

    const closedTimer = window.setInterval(() => {
      if (!popup.closed) return;
      settle(hasHubSpotAuthToken() ? { status: 'connected' } : { status: 'cancelled' });
    }, 500);

    window.addEventListener('message', handleMessage);
  });
}

export const getHubSpotContext = (): HubSpotContext => {
  const params = new URLSearchParams(window.location.search);

  // Fallback: als iframe geopend vanuit HubSpot record-pagina, parse uit referrer
  const fromReferrer =
    typeof document !== 'undefined' &&
    document.referrer &&
    document.referrer.includes('hubspot.com')
      ? parseFromHubSpotReferrer(document.referrer)
      : {};

  const portalIdFromToken = getPortalIdFromAuthToken();

  const portalId =
    portalIdFromToken ||
    params.get('portalId') ||
    params.get('portal_id') ||
    fromReferrer.portalId ||
    localStorage.getItem(PORTAL_STORAGE_KEY) ||
    import.meta.env.VITE_DEFAULT_PORTAL_ID;
  const dealId =
    params.get('dealId') ||
    params.get('deal_id') ||
    fromReferrer.dealId ||
    localStorage.getItem(DEAL_STORAGE_KEY) ||
    import.meta.env.VITE_DEFAULT_DEAL_ID;

  if (portalId) {
    localStorage.setItem(PORTAL_STORAGE_KEY, portalId);
  }

  if (dealId) {
    localStorage.setItem(DEAL_STORAGE_KEY, dealId);
  }

  return { portalId: portalId || undefined, dealId: dealId || undefined };
};

export const useHubSpotContext = () => getHubSpotContext();
